import {
  AccountProductEnum,
  SlCollapsibleListComponent,
  SLComponentTypenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import {
  isPositiveValue,
  TransactionsProps,
} from 'features/accounts/AccountDetails/AccountDetails.helpers';
import NoTransactions from 'features/accounts/transactions/NoTransactions/NoTransactions';
import useGetRecentTransaction from 'features/accounts/transactions/RecentTransactions/hooks/useGetRecentTransaction';
import UpcomingTransactions from 'features/accounts/transactions/UpcomingTransactions/UpcomingTransactions';
import usePayOverTimeTransactionTaggedAnalytics from 'features/payOverTime/hooks/usePayOverTimeTransactionTaggedAnalytics';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { propsFromTag } from 'shared/components/vxl/Tag/Tag.helpers';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

export interface ChildRef {
  fetchData: () => void;
  loadingMoreData?: boolean;
}

const customAttributes = (
  isAdvanceProduct: boolean,
  amount: string,
  accountType: string | undefined,
) => {
  if (isAdvanceProduct) {
    return {
      category: 'varo_advance',
      account_type: 'advance',
      amount: amount.replace('$', ''),
    };
  } else if (accountType) {
    return {
      category: accountType.toLowerCase(),
      account_type: accountType.toLowerCase(),
    };
  }
};

const RecentTransactions = forwardRef<ChildRef, TransactionsProps>(
  (
    {
      accountId,
      cachedTransactions,
      fetchTransactions,
      accountType,
    }: TransactionsProps,
    ref,
  ) => {
    const router = useRouter();
    const { t: translatedText } = useTranslation('accounts', {
      keyPrefix: 'transactions',
    });

    const {
      transactions,
      transactionError,
      loadingTransactions,
      fetchData,
      loadingMoreData,
    } = useGetRecentTransaction(accountId, fetchTransactions);

    useImperativeHandle(ref, () => ({ fetchData, loadingMoreData }));

    const handleOnPress = (transactionId: string) => {
      if (!fetchTransactions) {
        return;
      }
      router.push({
        pathname: '/accounts/transaction-details',
        params: {
          transactionId,
          accountType,
        },
      });
    };

    const transactionList = cachedTransactions?.length
      ? cachedTransactions
      : transactions;

    usePayOverTimeTransactionTaggedAnalytics(transactionList ?? undefined);

    const isAdvanceProduct = accountType === AccountProductEnum.Advance;

    return (
      <>
        <ScreenTransitionOverlay
          isLoading={loadingTransactions}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            transactionError && !loadingTransactions ? (
              <View style={styles.errorHandler}>
                <ErrorScreen
                  {...transactionError}
                  content={
                    <View style={styles.cancelTab}>
                      <Button
                        {...transactionError.props}
                        type="PRIMARY"
                        width="FULL"
                      />
                    </View>
                  }
                />
              </View>
            ) : null
          }
        />

        {transactionList?.length ? (
          <View style={styles.container}>
            {transactionList?.map((listItem) => {
              const listTitle = listItem.title.copy;
              if (
                listItem.__typename ===
                SLComponentTypenames.SLCollapsibleListComponent
              ) {
                return (
                  <UpcomingTransactions
                    component={listItem as SlCollapsibleListComponent}
                    key={`collapsible-${listItem.id}`}
                    handleOnPress={handleOnPress}
                    accountType={accountType}
                  />
                );
              }

              return (
                <View key={listItem.id} style={styles.innerContainer}>
                  <BodySmBoldText numberOfLines={0} style={styles.title}>
                    {listTitle}
                  </BodySmBoldText>
                  {listItem?.children?.map((item, index) => {
                    const tags = item.tags.map(propsFromTag).filter(isNotNil);
                    return (
                      <View key={item.id}>
                        {index > 0 ? (
                          <View style={styles.divider}>
                            <HairLineDivider />
                          </View>
                        ) : null}
                        <AmplitudeMask>
                          <TransactionListItem
                            amountStyle={isPositiveValue(
                              item.primaryDetail.copy,
                            )}
                            isPending={
                              listTitle === translatedText('pendingText')
                            }
                            isDisabled={
                              listTitle === translatedText('pendingText')
                            }
                            amountText={item.primaryDetail.copy}
                            titleText={item.primaryText.copy}
                            balanceText={item?.secondaryDetail?.copy}
                            accessibilityLabel=""
                            banner={{
                              body:
                                item?.callout?.children
                                  ?.map((c) => c.html)
                                  .join('') ?? '',
                            }}
                            onPress={() =>
                              handleOnPress(
                                item?.onClick?.transaction?.id ?? '',
                              )
                            }
                            secondaryText={item?.secondaryText?.copy}
                            analyticsId={
                              isAdvanceProduct
                                ? 'vaTransactionViewed'
                                : 'transactionViewed'
                            }
                            customAttributes={customAttributes(
                              isAdvanceProduct,
                              item.primaryDetail.copy,
                              accountType,
                            )}
                            tags={tags}
                          />
                        </AmplitudeMask>
                      </View>
                    );
                  })}
                </View>
              );
            })}
            <View style={styles.containerCentered}>
              {loadingMoreData ? <LoadingSpinner /> : null}
            </View>
          </View>
        ) : (
          <NoTransactions title={translatedText('recentTransactions')} />
        )}
      </>
    );
  },
);

const styles = StyleSheet.create({
  cancelTab: { marginTop: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.surface,
    flexDirection: 'column',
    flexShrink: 1,
    gap: theme.spacing.s,
  },
  containerCentered: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    height: 40,
    justifyContent: 'flex-start',
  },
  divider: { paddingHorizontal: theme.spacing.m },
  errorHandler: { justifyContent: 'space-between' },
  innerContainer: { backgroundColor: theme.color.background.primary },
  title: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default RecentTransactions;
